import { createContext, useContext, useMemo, useCallback, useState, useEffect } from 'react';

import { getItemFromLocalStorage, useLocalStorage } from './useLocalStorage';
import { FindUsersPermissionsUserByIdV5Query, School, UsersPermissionsUser } from '@edwin-group/people-graphql';
import { getUserContext, trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import { Authentication, AuthenticationContextType, AuthenticationProviderProps } from './types';
import { usePickUpWhereYouLeftOff } from '@/pages/home/hooks/usePickUpWhereYouLeftOff';
import { logoutSession } from '@/services/auth/auth.service';
import { getAccessToken, setAccessToken, clearAccessToken } from '@/services/auth/token-store';
import { refreshAccessToken } from '@/services/auth/refresh';

export const AuthenticationContext = createContext({} as AuthenticationContextType);

export const AuthenticationProvider = ({ children }: AuthenticationProviderProps) => {
  const [authentication, setAuthentication] = useLocalStorage<Authentication>('authentication', {
    isAuthenticated: false,
    accessToken: '',
    user: {} as Omit<UsersPermissionsUser, 'school'>,
  });

  const [schoolContext, setSchoolContext] = useLocalStorage<School | null>('schoolContext', null);
  const { setLastChapterOpened } = usePickUpWhereYouLeftOff();

  const updateUserContext = useCallback(
    (data: FindUsersPermissionsUserByIdV5Query | null) => {
      const userData = JSON.parse(JSON.stringify(data?.usersPermissionsUser)) as UsersPermissionsUser | null;
      const mySchools = data?.fetchMySchools;
      const schoolContextSelected = mySchools?.find(
        (school) => school.documentId === (schoolContext?.documentId || ''),
      );

      // if the school selected not exists, set the school context to the school from CMS
      const school = !schoolContextSelected ? userData?.school : schoolContextSelected;
      if (userData) {
        userData.school = school as School;
      }

      setSchoolContext(school as School);
      setAuthentication({
        accessToken: '', // never persisted; see getAuthentication
        isAuthenticated: authentication?.isAuthenticated || false,
        user: userData as UsersPermissionsUser,
      });

      trackGAEvent('user_context', getUserContext(userData as UsersPermissionsUser));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schoolContext?.documentId],
  );

  const updateSchoolContext = useCallback(
    (school: School) => {
      const newUserData = { ...authentication?.user, school };
      setAuthentication({
        accessToken: '', // never persisted; see getAuthentication
        isAuthenticated: authentication?.isAuthenticated || false,
        user: newUserData as UsersPermissionsUser,
      });

      setSchoolContext(school);
      trackGAEvent('user_context', getUserContext(newUserData as UsersPermissionsUser));
    },
    [authentication, setAuthentication, setSchoolContext],
  );

  const loginAuth = useCallback(
    async (authenticationCallback: Authentication) => {
      // Memory only. The matching refresh token arrived as an httpOnly cookie
      // the browser stores for us and JavaScript cannot read.
      setAccessToken(authenticationCallback.accessToken);

      setAuthentication({
        isAuthenticated: true,
        accessToken: '', // never persisted; see getAuthentication
        user: authenticationCallback.user,
      });

      setSchoolContext((authenticationCallback.user as UsersPermissionsUser).school || null);
    },
    [setAuthentication, setSchoolContext],
  );

  const toggleOnboardingStatus = useCallback(
    (onboarding: 'onboardLeadershipWheelCompleted' | 'onboardHomeCompleted') => {
      setAuthentication({
        accessToken: '', // never persisted; see getAuthentication
        isAuthenticated: authentication?.isAuthenticated || false,
        user: { ...authentication?.user, [onboarding]: !authentication?.user?.[onboarding] } as UsersPermissionsUser,
      });
    },
    [authentication, setAuthentication],
  );

  const logout = useCallback(async () => {
    // Server first: this destroys the session so the token cannot be replayed.
    // Awaited rather than fired-and-forgotten, because the redirect below tears
    // down the page and would cancel an in-flight request.
    await logoutSession();

    clearAccessToken();
    setAuthentication({
      isAuthenticated: false,
      accessToken: '',
      user: {} as UsersPermissionsUser,
    });
    setLastChapterOpened(null);
    setSchoolContext(null);
    window.location.href = '/login';
  }, [setAuthentication, setSchoolContext, setLastChapterOpened]);

  // A hard reload starts with an empty in-memory token while localStorage still
  // says the user is signed in. Trade the refresh cookie for a fresh access
  // token before rendering, or every query on the first paint fires
  // unauthenticated and bounces the user to /login.
  const [bootstrapped, setBootstrapped] = useState(() => !authentication?.isAuthenticated);

  useEffect(() => {
    if (bootstrapped || getAccessToken()) {
      setBootstrapped(true);
      return;
    }

    let cancelled = false;

    refreshAccessToken()
      // A dead or expired session is an ordinary outcome here, not an error to
      // surface: rendering proceeds and the normal unauthenticated flow applies.
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setBootstrapped(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // Runs once on mount: this is session rehydration, not a subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      // Merged from memory so consumers reading `authentication.accessToken`
      // see the live token even though it is never persisted.
      authentication: authentication ? { ...authentication, accessToken: getAccessToken() } : authentication,
      schoolContext,
      toggleOnboardingStatus,
      updateUserContext,
      updateSchoolContext,
      loginAuth,
      logout,
    }),
    [
      authentication,
      schoolContext,
      updateUserContext,
      updateSchoolContext,
      loginAuth,
      logout,
      toggleOnboardingStatus,
      // Included so the merged access token above is recomputed once the
      // bootstrap refresh lands.
      bootstrapped,
    ],
  );

  if (!bootstrapped) {
    return null;
  }

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

export const getAuthentication = () => {
  const stored = (getItemFromLocalStorage<Authentication>('authentication') || {}) as Authentication;

  // The access token lives in memory, never in localStorage - an httpOnly
  // refresh cookie buys nothing if a working bearer token sits in storage
  // beside it. It is merged back in here so the many existing
  // `getAuthentication()?.accessToken` call sites keep working unchanged.
  return { ...stored, accessToken: getAccessToken() } as Authentication;
};

export const getSchoolContext = () => {
  return (getItemFromLocalStorage<School>('schoolContext') || null) as School | null;
};

export const useAuthentication = () => {
  return useContext(AuthenticationContext);
};
